import React, { useEffect, useState } from "react";
import CommonSplitView from "presentational/common/CommonSplitView";
import Cropper from "react-easy-crop";
import IconButtonView from "presentational/common/IconButtonView";
import CommonGuideTooltipView from "presentational/common/modals/CommonGuideTooltipView";
import TXT from "presentational/common/multinational/TXT";
import { getDeviceType } from "lib/deviceInfo";
import config from "lib/fr-config";
import PropTypes from "prop-types";

const desktopCss = config.css.desktop;
const mobileCss = config.css.mobile;
const cookieNameIndex = config.cookieNameIndex;

ImageCropperPanelView.propTypes = {
  originalImageElem: PropTypes.object,
  originalImageDataURL: PropTypes.string,
  cropPixels: PropTypes.object,
  imageRatio: PropTypes.number.isRequired,
  minImgWidth: PropTypes.number.isRequired,
  minImgHeight: PropTypes.number.isRequired,
  onLoadRawImage: PropTypes.func.isRequired,
  onCrop: PropTypes.func.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  hasAddImageButtonClickedAtLeastOnce: PropTypes.bool.isRequired,
  onAddImageBtnClick: PropTypes.func.isRequired
};

function ImageCropperPanelView(props) {
  const {
    originalImageElem,
    originalImageDataURL,
    cropPixels,
    imageRatio,
    minImgWidth,
    minImgHeight,
    onLoadRawImage,
    onCrop,
    onCancelClick,
    onNextClick,
    hasAddImageButtonClickedAtLeastOnce,
    onAddImageBtnClick
  } = props;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [maxZoomValue, setMaxZoomValue] = useState(1);
  const [initialCroppedArea, setInitialCroppedArea] = useState(undefined);
  const [wholeVerticalMargin, setWholeVerticalMargin] = useState(110);
  const [needSubmitTooltip /** , setNeedSubmitTooltip */] = useState(
    !hasAddImageButtonClickedAtLeastOnce
  );

  useEffect(() => {
    setSavedLastZoomValue();
  }, []);
  useEffect(() => {
    initNewImg();
  }, [originalImageElem]);

  /**
   * viewport의 세로 전체길이의 픽셀 이용시 "100vh", "100dvh" 사용하는데,
   * [모바일에서는] -> (dvh 사용하자)
   * dvh는 모바일(특히 사파리에서) 주소창에 의해 viewport가 늘어나던 줄던 모두 대응됨.
   * vh는 무조건 화면 액정 길이만큼 대응해서 사용됨
   * [데스크탑에서는] -> (vh 사용하자)
   * vh는 크롬에서는 dvh, vh 둘다 똑같이 viewport 전부 사용되어 차이 전혀없음.
   * 하지만 사파리에서는 dvh, vh 다르게 나타나며 심지어 dvh는 이상하게 표현된다
   */

  const cssXL = {
    root: { height: `calc(100vh - ${wholeVerticalMargin}px)` }
  };
  const cssXS = {
    root: {
      marginTop: -mobileCss.paddingBetweenHeaderbarAndFirstContent
    },
    imageWrapper: {
      position: "relative",
      height: `calc(100dvh - env(safe-area-inset-bottom) - ${mobileCss.logoBarHeightPX}px - ${mobileCss.headerBarHeightPX}px)`
    },
    buttonArea: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1100,
      backgroundColor: "white",

      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",

      height: `calc(env(safe-area-inset-bottom) + ${mobileCss.headerBarHeightPX}px)`
    },
    buttonWrapper: {
      paddingBottom: "calc(env(safe-area-inset-bottom))"
    }
  };

  const getRawImgRatio = elem => {
    const { width, height } = elem;
    return width / height;
  };

  const setSavedLastZoomValue = () => {
    const { x, y, width, height } = cropPixels;
    if (
      typeof x === "number" &&
      typeof y === "number" &&
      typeof width === "number" &&
      typeof height === "number"
    ) {
      setInitialCroppedArea(cropPixels);
    }
  };

  const initNewImg = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    calculateMaxZoomValue();
  };

  const calculateMaxZoomValue = () => {
    if (!originalImageElem) return null;

    const { width, height } = originalImageElem;
    const maxZoomOfHeight = height / minImgHeight;
    const maxZoomOfWidth = width / minImgWidth;
    setMaxZoomValue(Math.min(maxZoomOfHeight, maxZoomOfWidth));
  };

  const getWidthOfLeft = newWholeVerticalMargin => {
    if (!originalImageElem) return null;
    if (!newWholeVerticalMargin) {
      console.log("error");
      console.error("error");
      return null;
    }

    const imgElemRatio = getRawImgRatio(originalImageElem);
    const { innerHeight } = window;
    const maxHeight = innerHeight - newWholeVerticalMargin;
    const maxWidth = maxHeight * imgElemRatio;

    return maxWidth;
  };

  const getWidthOfLeftSideForDesktop = newWholeVerticalMargin => {
    if (wholeVerticalMargin !== newWholeVerticalMargin) {
      setWholeVerticalMargin(newWholeVerticalMargin);
    }

    const width = getWidthOfLeft(newWholeVerticalMargin);
    if (!width) return null;
    return width;
  };

  const handleCropChange = value => {
    setCrop(value);
  };

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    onCrop(croppedAreaPixels);
  };

  const handleZoomChange = value => {
    setZoom(value);
  };

  const handleImgChange = e => {
    setInitialCroppedArea(undefined);
    onLoadRawImage(e);
  };

  const handleAddImageBtnClick = () => {
    onAddImageBtnClick();
  };

  const desktopLayout = () => {
    return (
      <div>
        <CommonSplitView
          getWidthOfLeftSide={getWidthOfLeftSideForDesktop}
          leftSideComponent={
            <div style={cssXL.root}>
              {originalImageDataURL && (
                <Cropper
                  image={originalImageDataURL}
                  crop={crop}
                  zoom={zoom}
                  aspect={imageRatio}
                  maxZoom={maxZoomValue}
                  onCropChange={handleCropChange}
                  onCropComplete={handleCropComplete}
                  onZoomChange={handleZoomChange}
                  initialCroppedAreaPixels={initialCroppedArea}
                />
              )}
            </div>
          }
          rightSideComponent={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: desktopCss.standardPaddingBetweenContent
                }}
              >
                <div>
                  <IconButtonView type={"close"} onClick={onCancelClick} />
                </div>
                <div>
                  <input
                    name="myFile"
                    type="file"
                    accept="image/*"
                    id="image-input"
                    onChange={handleImgChange}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="image-input">
                    <CommonGuideTooltipView
                      needClose={hasAddImageButtonClickedAtLeastOnce}
                      needCountUp={
                        hasAddImageButtonClickedAtLeastOnce && needSubmitTooltip
                      }
                      message={<TXT name="msg_tooltipClickNormalBtn" />}
                      placement={"top"}
                      arrow
                      backgroundDesign={"primary"}
                      cookieName={cookieNameIndex.imgAddBtnGuide.name}
                      maxDisplayCount={cookieNameIndex.imgAddBtnGuide.count}
                    >
                      <IconButtonView
                        type={"add"}
                        onClick={handleAddImageBtnClick}
                      />
                    </CommonGuideTooltipView>
                  </label>
                </div>
                <div>
                  <IconButtonView
                    type={"next"}
                    disableRightMargin
                    onClick={onNextClick}
                  />
                </div>
              </div>
            </div>
          }
        />
      </div>
    );
  };

  const mobileLayout = () => {
    return (
      <div style={cssXS.root}>
        <div style={cssXS.imageWrapper}>
          {originalImageElem && (
            <Cropper
              image={originalImageDataURL}
              crop={crop}
              zoom={zoom}
              aspect={imageRatio}
              maxZoom={maxZoomValue}
              onCropChange={handleCropChange}
              onCropComplete={handleCropComplete}
              onZoomChange={handleZoomChange}
              initialCroppedAreaPixels={initialCroppedArea}
            />
          )}
        </div>
        <div style={cssXS.buttonArea}>
          <div style={cssXS.buttonWrapper}>
            <IconButtonView type={"close"} onClick={onCancelClick} />
          </div>
          <div style={cssXS.buttonWrapper}>
            <input
              name="myFile"
              type="file"
              accept="image/*"
              id="image-input"
              onChange={handleImgChange}
              style={{ display: "none" }}
            />
            <label style={{ display: "inline-block" }} htmlFor="image-input">
              <CommonGuideTooltipView
                needClose={hasAddImageButtonClickedAtLeastOnce}
                needCountUp={
                  hasAddImageButtonClickedAtLeastOnce && needSubmitTooltip
                }
                message={<TXT name="msg_tooltipClickNormalBtn" />}
                placement={"top"}
                arrow
                backgroundDesign={"primary"}
                cookieName={cookieNameIndex.imgAddBtnGuide.name}
                maxDisplayCount={cookieNameIndex.imgAddBtnGuide.count}
              >
                <IconButtonView type={"add"} onClick={handleAddImageBtnClick} />
              </CommonGuideTooltipView>
            </label>
          </div>
          <div style={cssXS.buttonWrapper}>
            <IconButtonView type={"next"} onClick={onNextClick} />
          </div>
        </div>
      </div>
    );
  };

  return getDeviceType() === "desktop" ? desktopLayout() : mobileLayout();
}

export default ImageCropperPanelView;
